import React from 'react';
import { channelColor, channelLabel, formatNumber, formatPercent } from '../hooks/useApi';
import { EmptyState } from './ui';

/**
 * The channel ledger.
 *
 * One continuous tape across the width, segmented by channel and proportional
 * to volume — a tally strip rather than a pie. It reads left to right like a
 * register line, and doubles as the dashboard's filter: click a segment or a
 * key and everything below narrows to that channel.
 *
 * `metric` switches what the tape measures. Visits answers "who arrived";
 * leads answers "who was worth having" — the two rarely look the same, and
 * seeing the tape change shape between them is the point.
 */
export default function ChannelLedger({
  channels = [],
  totals,
  metric = 'visits',
  onMetricChange,
  selected,
  onSelect,
}) {
  const visible = channels.filter((c) => c[metric] > 0);
  const total = visible.reduce((sum, c) => sum + c[metric], 0);

  if (!total) {
    return (
      <EmptyState
        icon="fa-chart-simple"
        title="No activity in this period"
        text="Once the site receives visits, the split by channel appears here."
      />
    );
  }

  return (
    <div>
      <div className="spa-row" style={{ marginBottom: 14 }}>
        <div className="spa-seg" role="group" aria-label="Ledger metric">
          {[
            ['visits', 'Visits'],
            ['leads', 'Leads'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={metric === key ? 'is-on' : ''}
              aria-pressed={metric === key}
              onClick={() => onMetricChange(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div style={{ marginLeft: 'auto', fontSize: '0.82rem', color: '#667085' }}>
          <span className="spa-num" style={{ fontWeight: 600, color: '#14161a' }}>
            {formatNumber(total)}
          </span>{' '}
          {metric === 'visits' ? 'visits' : 'leads'} across {visible.length} channels
        </div>
      </div>

      {/* --- the tape --- */}
      <div className="spa-ledger__tape" role="img"
        aria-label={`${metric} by channel: ${visible.map((c) => `${channelLabel(c.channel)} ${c[metric]}`).join(', ')}`}>
        {visible.map((c) => {
          const share = (c[metric] / total) * 100;
          const isDim = selected && selected !== c.channel;
          return (
            <button
              key={c.channel}
              type="button"
              className={`spa-ledger__seg${isDim ? ' is-dim' : ''}`}
              style={{ width: `${share}%`, background: channelColor(c.channel) }}
              onClick={() => onSelect(selected === c.channel ? null : c.channel)}
              title={`${channelLabel(c.channel)} — ${formatNumber(c[metric])} (${share.toFixed(1)}%)`}
              aria-label={`${channelLabel(c.channel)}, ${formatPercent(share)}`}
            >
              {/* Only label a segment wide enough to hold the text. */}
              {share >= 9 && <span className="spa-ledger__seg__pct">{Math.round(share)}%</span>}
            </button>
          );
        })}
      </div>

      {/* --- the keys, which are also the filter --- */}
      <div className="spa-ledger__keys">
        {channels.map((c) => (
          <button
            key={c.channel}
            type="button"
            className={`spa-ledger__key${selected === c.channel ? ' is-on' : ''}`}
            style={{ '--seg': channelColor(c.channel) }}
            onClick={() => onSelect(selected === c.channel ? null : c.channel)}
            aria-pressed={selected === c.channel}
          >
            <span className="spa-ledger__key__name">
              <span className="spa-ledger__swatch" aria-hidden="true" />
              {channelLabel(c.channel)}
            </span>
            <span className="spa-ledger__key__figs">
              <span className="spa-ledger__key__visits">{formatNumber(c.visits)}</span>
              <span className="spa-ledger__key__leads">
                {formatNumber(c.leads)} leads · {formatPercent(c.conversion_rate, 1)}
              </span>
            </span>
          </button>
        ))}
      </div>

      {totals && (
        <p className="spa-card__note" style={{ marginTop: 14 }}>
          {selected
            ? `Showing ${channelLabel(selected)} only. Click it again to clear the filter.`
            : 'Click a channel to filter everything below it.'}
        </p>
      )}
    </div>
  );
}
